import { 
    gql, 
    useMutation, 
    ApolloError 
} from "@apollo/client";
import { UpdateCommunityMutationMutation, UpdateCommunityMutationMutationVariables } from "../../../../schemaTypes";

export const updateCommunityMutation = gql`
  mutation UpdateCommunityMutation(
    $communityId: String!
    $input: UpdateCommunityInput!
  ) {
    updateCommunity(
      communityId: $communityId,
      input: $input
    ) {
      errors {
        path
        message
      }
      message
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: UpdateCommunityMutationMutationVariables & { logoUrlFile?: File }
      ) => Promise<{
        errors: ({ path: string; message: string } | null)[] | null;
        message?: string;
      }>;
      loading: boolean;
      error?: ApolloError;
    }
  ) => React.ReactElement | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const UpdateCommunityController: React.FC<Props> = ({ children, onMessage }) => {
  const [updateCommunity, { loading, error }] = useMutation<
    UpdateCommunityMutationMutation,
    UpdateCommunityMutationMutationVariables
  >(updateCommunityMutation);

  const submit = async (
    values: UpdateCommunityMutationMutationVariables & { logoUrlFile?: File }
  ) => {
    const { logoUrlFile, ...rest } = values;
    let logoUrlSrc = rest.input.logoUrl;
    if (logoUrlFile) {
      logoUrlSrc = await fileToBase64(logoUrlFile);
    }
    const { data } = await updateCommunity({
      variables: {
        communityId: rest.communityId,
        input: {
          ...rest.input,
          logoUrl: logoUrlSrc
        },
      },
    });

    const errors = data?.updateCommunity?.errors ?? null;
    const message = data?.updateCommunity?.message ?? undefined;

    if (message && onMessage) {
      onMessage(message);
    }

    return {
      errors,
      message,
    };
  };

  return children({ submit, loading, error });
};